import { useEffect, useRef } from 'react';
import { trackThemePageVisit } from '@/lib/themeVisitTracking';

// ── Suivi de visite d'une page de thème publique ─────────────────────
// Enregistre :
//   • une vue à l'ouverture (compteur de visites) ;
//   • le temps passé à la sortie (durée en secondes) ;
//   • des « pings » de comportement toutes les 30 s pour les sessions longues.
// L'IP réelle est ajoutée côté serveur par l'Edge Function.

const PING_INTERVAL_MS = 30_000;

export function useThemePageTracking(
  pageKey: string | null | undefined,
  boutiqueId: number | null | undefined,
  enabled = true,
): void {
  const startRef = useRef<number>(0);
  const sentRef = useRef(false);

  useEffect(() => {
    if (!enabled || !pageKey) return;

    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    startRef.current = Date.now();
    sentRef.current = false;

    // 1. Vue de la page
    void trackThemePageVisit({
      boutique_id: boutiqueId ?? null,
      page_key: pageKey,
      path,
      event: 'view',
    });

    const elapsedSeconds = () => Math.round((Date.now() - startRef.current) / 1000);

    // 2. Pings périodiques (comportement / sessions longues)
    const pingTimer = window.setInterval(() => {
      void trackThemePageVisit({
        boutique_id: boutiqueId ?? null,
        page_key: pageKey,
        path,
        event: 'ping',
        duration_seconds: elapsedSeconds(),
      });
    }, PING_INTERVAL_MS);

    // 3. Durée passée à la sortie
    const sendLeave = () => {
      if (sentRef.current) return;
      sentRef.current = true;
      void trackThemePageVisit({
        boutique_id: boutiqueId ?? null,
        page_key: pageKey,
        path,
        event: 'leave',
        duration_seconds: elapsedSeconds(),
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') sendLeave();
    };

    window.addEventListener('pagehide', sendLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      sendLeave();
      window.clearInterval(pingTimer);
      window.removeEventListener('pagehide', sendLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pageKey, boutiqueId, enabled]);
}